/**
 * Utility functions for detecting which row/column a grid line intersects
 * Consolidates the word-based detection logic used by both row and column splitting
 */
import { calculateWordBounds } from "./geometryCalculations";

/**
 * Find which row or column index a grid line intersects by finding words near the grid line
 * @param {number} gridLinePosition - The coordinate of the new grid line (X for vertical, Y for horizontal)
 * @param {'vertical'|'horizontal'} axis - The axis of the grid line
 * @param {Object} selectedTable - The table data with word positions
 * @param {Object} pdfPageDimensions - PDF page dimensions for coordinate normalization
 * @param {number} currentPage - The current page to analyze (for page-specific detection)
 * @returns {number} The detected row or column index, or 0 if no words found
 */
export const detectGridLineSplitIndex = (
  gridLinePosition,
  axis,
  selectedTable,
  pdfPageDimensions,
  currentPage = null
) => {
  const isVertical = axis === "vertical";

  if (!pdfPageDimensions?.current) {
    return 0;
  }

  const { width: pdfWidth, height: pdfHeight } = pdfPageDimensions.current;

  if (!pdfWidth || !pdfHeight) {
    return 0;
  }

  if (isVertical) {
    // COLUMN DETECTION LOGIC (for vertical lines)
    const columnBounds = {}; // colIndex -> {minX, maxX, wordCount}

    // First pass: Calculate the bounds of each column
    Object.keys(selectedTable || {}).forEach((cellKey) => {
      if (cellKey.match(/^\d+_\d+$/)) {
        const cellData = selectedTable[cellKey];

        if (cellData && cellData[0] && cellData[0].words) {
          // For column detection, we need words from ALL pages of a multi-page table
          // Don't filter by page for column detection - columns span across pages

          const [, colStr] = cellKey.split("_");
          const colIndex = parseInt(colStr);

          cellData[0].words.forEach((word) => {
            const bounds = calculateWordBounds(word.bbox, pdfPageDimensions);
            if (!bounds) return;

            // Track the bounds of this column
            if (!columnBounds[colIndex]) {
              columnBounds[colIndex] = {
                minX: bounds.left,
                maxX: bounds.right,
                wordCount: 0,
              };
            } else {
              columnBounds[colIndex].minX = Math.min(
                columnBounds[colIndex].minX,
                bounds.left
              );
              columnBounds[colIndex].maxX = Math.max(
                columnBounds[colIndex].maxX,
                bounds.right
              );
            }
            columnBounds[colIndex].wordCount++;
          });
        }
      }
    });

    // Second pass: Find which column the grid line falls within
    let bestColumn = 0;
    let bestScore = -1;

    Object.keys(columnBounds).forEach((colStr) => {
      const colIndex = parseInt(colStr);
      const bounds = columnBounds[colIndex];

      // Check if grid line falls within this column's bounds
      if (gridLinePosition >= bounds.minX && gridLinePosition <= bounds.maxX) {
        // Score based on word count (more words = more confident)
        const score = bounds.wordCount;
        if (score > bestScore) {
          bestScore = score;
          bestColumn = colIndex;
        }
      }
    });

    // If no column found, this indicates the grid line is between columns
    if (bestColumn === 0 && bestScore === -1) {
      // Fallback: Find the closest column to the right of the grid line
      let closestColumn = 0;
      let minDistance = Infinity;

      Object.keys(columnBounds).forEach((colStr) => {
        const colIndex = parseInt(colStr);
        const bounds = columnBounds[colIndex];

        // Distance to the left edge of the column
        const distance = Math.abs(bounds.minX - gridLinePosition);
        if (distance < minDistance) {
          minDistance = distance;
          closestColumn = colIndex;
        }
      });

      bestColumn = closestColumn;
    }

    return bestColumn;
  } else {
    // ROW DETECTION LOGIC (for horizontal lines)

    // Row operations are ALWAYS page-specific - if no page specified, return 0
    if (currentPage === null || currentPage === undefined) {
      return 0;
    }

    const rowBounds = {}; // rowIndex -> {minY, maxY, wordCount, centerY}

    // First pass: Calculate the bounds of each row
    Object.keys(selectedTable || {}).forEach((cellKey) => {
      if (cellKey.match(/^\d+_\d+$/)) {
        const cellData = selectedTable[cellKey];

        if (cellData && cellData[0] && cellData[0].words) {
          const cellPage = cellData[0].page;

          // For row detection, only process current page (rows are page-specific)
          // Convert both to numbers for proper comparison
          const cellPageNum = parseInt(cellPage);
          const currentPageNum = parseInt(currentPage);

          if (currentPage !== null && cellPageNum !== currentPageNum) {
            return;
          }

          const [rowStr] = cellKey.split("_");
          const rowIndex = parseInt(rowStr);

          cellData[0].words.forEach((word) => {
            const bounds = calculateWordBounds(word.bbox, pdfPageDimensions);
            if (!bounds) return;

            // Track the bounds of this row
            if (!rowBounds[rowIndex]) {
              rowBounds[rowIndex] = {
                minY: bounds.top,
                maxY: bounds.bottom,
                centerY: bounds.centerY,
                wordCount: 0,
              };
            } else {
              rowBounds[rowIndex].minY = Math.min(
                rowBounds[rowIndex].minY,
                bounds.top
              );
              rowBounds[rowIndex].maxY = Math.max(
                rowBounds[rowIndex].maxY,
                bounds.bottom
              );
              rowBounds[rowIndex].centerY =
                (rowBounds[rowIndex].centerY * rowBounds[rowIndex].wordCount +
                  bounds.centerY) /
                (rowBounds[rowIndex].wordCount + 1);
            }
            rowBounds[rowIndex].wordCount++;
          });
        }
      }
    });

    // Find which row the horizontal grid line should split
    // The split line should be positioned between two rows
    // We want to find the row that is ABOVE the split line (this row will be split)
    let splitRow = 0;
    let bestDistance = Infinity;

    Object.keys(rowBounds).forEach((rowStr) => {
      const rowIndex = parseInt(rowStr);
      const bounds = rowBounds[rowIndex];

      // Check if the split line is below this row's center
      // This would mean this row should be split
      if (gridLinePosition > bounds.centerY) {
        const distance = gridLinePosition - bounds.centerY;

        if (distance < bestDistance) {
          bestDistance = distance;
          splitRow = rowIndex;
        }
      }
    });

    return splitRow;
  }
};
